// src/context/LoggingContext.js
import React, { createContext, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import LoggingService, { LOG_LEVEL, LOG_CATEGORY } from '../services/LoggingService';

const LoggingContext = createContext();

export function useLogging() {
  return useContext(LoggingContext);
}

export function LoggingProvider({ children }) {
  const { currentUser } = useAuth() || { currentUser: null };
  const userId = currentUser?.uid || null;

  // Wrap logging methods to automatically include userId
  const logError = (message, error = {}) => {
    return LoggingService.logError(message, error, userId);
  };

  const logUserInteraction = (action, data = {}) => {
    return LoggingService.logUserInteraction(action, data, userId);
  };

  const logNavigation = (from, to) => {
    return LoggingService.logNavigation(from, to, userId);
  };

  const logAuth = (action, data = {}) => {
    return LoggingService.logAuth(action, userId, data);
  };

  // Custom log method for flexibility
  const log = (level, category, message, data = {}) => {
    return LoggingService.log(level, category, message, data, userId);
  };

  // Global error handler
  useEffect(() => {
    const originalConsoleError = console.error;
    
    // Override console.error to log errors to Firestore
    console.error = (...args) => {
      // Call original console.error
      originalConsoleError.apply(console, args);
      
      // Log to Firestore
      const errorMessage = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');
      
      logError('Console error', { message: errorMessage });
    };

    // Set up global error handler
    const handleGlobalError = (event) => {
      const { message, filename, lineno, colno, error } = event;
      logError('Uncaught exception', {
        message,
        source: filename,
        line: lineno,
        column: colno,
        stack: error?.stack,
      });
    };

    // Set up unhandled promise rejection handler
    const handleUnhandledRejection = (event) => {
      logError('Unhandled promise rejection', {
        message: event.reason?.message || 'Unknown promise rejection',
        stack: event.reason?.stack,
      });
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      console.error = originalConsoleError;
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [userId]);

  const value = {
    log,
    logError,
    logUserInteraction,
    logNavigation,
    logAuth,
    LOG_LEVEL,
    LOG_CATEGORY
  };

  return (
    <LoggingContext.Provider value={value}>
      {children}
    </LoggingContext.Provider>
  );
}